// MyGarage認証API
interface MyGarageLoginRequest {
  username: string;
  password: string;
}

// AISHA Admin Status API
interface AdminStatusResponse {
  frontend_user_id: string;
  is_admin: boolean;
}

interface MyGarageLoginResponse {
  data?: any; // 実際のAPIレスポンス構造を確認するために一時的にany型を使用
  message?: string;
  error?: {
    message: string;
  };
}

interface MyGarageUser {
  id: string;
  username: string;
  name: string;
  email?: string;
  credits?: number; // オプショナルに変更
  isAdmin?: boolean;
  personalSettings?: any;
}

const MGDRIVE_API_BASE_URL = import.meta.env.VITE_MGDRIVE_API_BASE_URL || 'https://api.mygare.jp/api';
const AISHA_API_BASE_URL = import.meta.env.VITE_AISHA_API_BASE || 'http://localhost:7999/api';

// MyGarage認証成功後のバックエンド登録処理
const registerMyGarageUserToBackend = async (mygarageData: any): Promise<void> => {
  try {
    console.log('MyGarage認証成功後のバックエンド登録処理を開始:', mygarageData);
    
    const response = await fetch(`${AISHA_API_BASE_URL}/mygarage-auth/register/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(mygarageData),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      console.error('MyGarage認証後のユーザー登録に失敗:', result);
      console.error('エラー詳細:', result.error);
      console.error('操作:', result.details?.operation);
      // エラーをログに出力するが、ログイン処理は継続
    } else {
      console.log('MyGarage認証後のユーザー登録成功:', result);
      if (result.is_new_user) {
        console.log('新規ユーザー登録完了 - 初期クレジット:', result.initial_credits);
      } else {
        console.log('既存ユーザー - 登録処理はスキップ');
      }
    }
  } catch (error) {
    console.error('MyGarage認証後のAPI呼び出しエラー:', error);
    // エラーが発生してもログイン処理は継続
  }
};

// AISHA APIから管理者ステータスを取得する関数
const fetchAdminStatus = async (userId: string): Promise<boolean> => {
  try {
    const response = await fetch(`${AISHA_API_BASE_URL}/users/${userId}/admin-status/`);
    if (response.ok) {
      const data: AdminStatusResponse = await response.json();
      return data.is_admin;
    }
  } catch (error) {
    console.warn('Failed to fetch admin status:', error);
  }
  return false;
};

export const myGarageLogin = async (username: string, password: string): Promise<MyGarageUser | null> => {
  try {
    // メールアドレスの形式チェック
    if (!username || !username.includes('@')) {
      throw new Error('メールアドレスの形式が正しくありません');
    }
    
    // パスワードの基本チェック
    if (!password || password.length < 1) {
      throw new Error('パスワードを入力してください');
    }
    
    // まずは実際のMyGarage APIにアクセスを試行
    // MyGarage APIの形式を複数試行
    let response: Response;
    
    // 新しいMyGarage API（api.mygare.jp）でログイン
    try {
      response = await fetch(`${MGDRIVE_API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode: 'chk_login_email',
          email: username,
          password,
        }),
      });
    } catch (error) {
      console.error('Login attempt failed:', error);
      throw error;
    }

    if (!response.ok) {
      // エラーレスポンスの詳細を確認
      let errorMessage = `ログインに失敗しました: ${response.status}`;
      try {
        const errorData = await response.text();
        console.error('MyGarage API error response:', errorData);
        
        // JSONレスポンスの場合は解析してメッセージを抽出
        try {
          const errorJson = JSON.parse(errorData);
          if (errorJson.error && errorJson.error.message) {
            // MyGarage APIからのエラーメッセージを分かりやすく変換
            const apiErrorMessage = errorJson.error.message;
            if (apiErrorMessage === 'Unauthorized' || response.status === 401) {
              errorMessage = 'メールアドレスまたはパスワードが正しくありません';
            } else {
              errorMessage = apiErrorMessage;
            }
          } else if (errorJson.message) {
            errorMessage = errorJson.message;
          }
        } catch (parseError) {
          // JSON解析に失敗した場合は生のレスポンスを使用
          if (response.status === 401) {
            errorMessage = 'メールアドレスまたはパスワードが正しくありません';
          } else {
            errorMessage += ` - ${errorData}`;
          }
        }
      } catch (e) {
        console.error('Could not parse error response');
      }
      
      // 404エラーの場合は、テスト用の実装を使用
      if (response.status === 404) {
        console.warn('MyGarage API not found, using test implementation');
        return handleTestLogin(username, password);
      }
      throw new Error(errorMessage);
    }

    // レスポンスの内容を確認
    const responseText = await response.text();
    console.log('MyGarage API response:', responseText);
    
    let data: MyGarageLoginResponse;
    try {
      data = JSON.parse(responseText);
      console.log('Parsed response data:', data);
      console.log('data.data:', data.data);
      console.log('data.data?.token:', data.data?.token);
      
      // レスポンスの全フィールドを確認
      if (data.data) {
        console.log('Available fields in data.data:', Object.keys(data.data));
        console.log('Full data.data object:', JSON.stringify(data.data, null, 2));
      }
    } catch (e) {
      console.error('Could not parse JSON response:', responseText);
      throw new Error('APIレスポンスの解析に失敗しました');
    }

    // 新しいAPI形式: { code: "OK", user_id: "231461", nickname: "Amemiya", email: "...", user: { ... } }
    // 旧API形式: { data: { ... } }
    const isNewApi = data.code === 'OK' || data.user_id;
    const userData = isNewApi ? data : data.data;

    if (userData) {
      // ユーザーIDの取得
      let userId = (isNewApi ? userData.user_id : (userData.created_by || userData.user_id || userData.id))?.toString();
      const userName = isNewApi ? (userData.nickname || userData.user?.auth_display_name) : userData.name;
      const userEmail = userData.email || '';

      // クッキーに保存（30日間有効）
      if (userId) {
        const expires = new Date();
        expires.setTime(expires.getTime() + 30 * 24 * 60 * 60 * 1000);
        const cookieValue = encodeURIComponent(JSON.stringify({
          id: userId,
          name: userName || 'MyGarageユーザー',
          email: userEmail
        }));
        document.cookie = `mygarage_user=${cookieValue};expires=${expires.toUTCString()};path=/`;
      }

      if (!userId || userId === 'unknown') {
        userId = 'unknown';
      }

      // 管理者ステータスを取得
      let isAdmin = await fetchAdminStatus(userId);
      if (userId === '200120') {
        isAdmin = true;
      }

      // バックエンドにユーザー登録処理を呼び出し
      await registerMyGarageUserToBackend(isNewApi ? {
        id: parseInt(userId),
        email: userEmail,
        name: userName,
        created_by: parseInt(userId),
      } : userData);

      // AISHAアプリ用のユーザーオブジェクトに変換
      const aishaUser: MyGarageUser = {
        id: userId,
        username: userEmail || 'unknown',
        name: userName || 'MyGarageユーザー',
        email: userEmail,
        isAdmin,
        personalSettings: {
          numberManagement: {},
          referenceRegistration: {
            carPhotos: [
              { viewAngle: 'front', label: 'フロント正面' },
              { viewAngle: 'side', label: 'サイド' },
              { viewAngle: 'rear', label: 'リア' },
              { viewAngle: 'front_angled_7_3', label: 'フロント斜め' },
              { viewAngle: 'rear_angled_7_3', label: 'リア斜め' }
            ]
          }
        },
      };

      console.log('Successfully created AISHA user:', aishaUser);
      return aishaUser;
    } else {
      // エラーレスポンスの処理
      console.log('Login failed - checking error conditions:');
      console.log('data.data exists:', !!data.data);
      console.log('data.error exists:', !!data.error);
      console.log('data.message exists:', !!data.message);
      
      let errorMessage = 'ログインに失敗しました';
      if (data.error && data.error.message) {
        const apiErrorMessage = data.error.message;
        if (apiErrorMessage === 'Unauthorized') {
          errorMessage = 'メールアドレスまたはパスワードが正しくありません';
        } else {
          errorMessage = apiErrorMessage;
        }
      } else if (data.message) {
        errorMessage = data.message;
      }
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('MyGarage login error:', error);
    // ネットワークエラーの場合もテスト用実装を使用
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.warn('Network error, using test implementation');
      return handleTestLogin(username, password);
    }
    throw error;
  }
};

// テスト用のログイン実装
const handleTestLogin = async (username: string, password: string): Promise<MyGarageUser | null> => {
  // 簡単なテストユーザーの実装
  const testUsers = [
    { id: '1', username: 'test@example.com', name: 'テストユーザー1', email: 'test@example.com' },
    { id: '2', username: 'demo@example.com', name: 'デモユーザー', email: 'demo@example.com' },
    { id: '3', username: 'sample@example.com', name: 'サンプルユーザー', email: 'sample@example.com' }
  ];

  // どんなパスワードでもログイン可能（テスト用）
  const user = testUsers.find(u => u.username === username || u.username.includes(username));
  
  if (user) {
    const testToken = `test-token-${Date.now()}`;
    localStorage.setItem('mygarage-token', testToken);
    
    // テストユーザーの管理者ステータスを取得
    let isAdmin = await fetchAdminStatus(user.id);
    
    // 一時的なフォールバック：ユーザー200120は強制的に管理者にする
    if (user.id === '200120') {
      isAdmin = true;
    }
    
    // テスト用データでバックエンドにユーザー登録処理を呼び出し
    const testMyGarageData = {
      id: parseInt(user.id),
      created_by: user.id,
      name: user.name,
      email: user.email
    };
    await registerMyGarageUserToBackend(testMyGarageData);
    
    const testUser = {
      id: user.id,
      username: user.username,
      name: user.name,
      email: user.email,
      isAdmin, // 管理者フラグを追加
      personalSettings: {
        numberManagement: {},
        referenceRegistration: {
          carPhotos: [
            { viewAngle: 'front', label: 'フロント正面' },
            { viewAngle: 'side', label: 'サイド' },
            { viewAngle: 'rear', label: 'リア' },
            { viewAngle: 'front_angled_7_3', label: 'フロント斜め' },
            { viewAngle: 'rear_angled_7_3', label: 'リア斜め' }
          ]
        }
      },
    };
    
    return testUser;
  }
  
  throw new Error('テストユーザーが見つかりません。test@example.com を試してください。');
};

export const myGarageLogout = async (): Promise<void> => {
  // クッキーを削除
  document.cookie = 'mygarage_user=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
  try { sessionStorage.removeItem('mygarage_user'); } catch (e) { /* noop */ }
};

export const validateMyGarageToken = async (): Promise<MyGarageUser | null> => {
  try {
    // クッキーから読み込み
    const cookies = document.cookie.split(';');
    let mygarageUser = null;

    for (const cookie of cookies) {
      const [name, ...valueParts] = cookie.trim().split('=');
      const value = valueParts.join('='); // 値に=が含まれる場合に対応
      if (name === 'mygarage_user' && value) {
        try {
          // まずデコードしてパースを試みる
          mygarageUser = JSON.parse(decodeURIComponent(value));
          break;
        } catch (e) {
          // デコードに失敗した場合、直接パースを試みる（古い形式のクッキー対応）
          try {
            mygarageUser = JSON.parse(value);
            break;
          } catch (e2) {
            console.error('Failed to parse cookie:', e, e2);
          }
        }
      }
    }
    
    // クッキーが取れない場合（iframe等の第三者クッキー制限）は sessionStorage から復元
    if (!mygarageUser) {
      try {
        const ss = sessionStorage.getItem('mygarage_user');
        if (ss) mygarageUser = JSON.parse(ss);
      } catch (e) {
        console.warn('sessionStorage復元失敗:', e);
      }
    }

    if (!mygarageUser || !mygarageUser.id) {
      return null;
    }

    // 管理者ステータスを取得
    let isAdmin = await fetchAdminStatus(mygarageUser.id);
    
    // 一時的なフォールバック：ユーザー200120は強制的に管理者にする
    if (mygarageUser.id === '200120') {
      isAdmin = true;
    }

    const aishaUser: MyGarageUser = {
      id: mygarageUser.id,
      username: mygarageUser.email || 'unknown',
      name: mygarageUser.name || 'MyGarageユーザー',
      email: mygarageUser.email || '',
      isAdmin,
      personalSettings: {
        numberManagement: {},
        referenceRegistration: {
          carPhotos: [
            { viewAngle: 'front', label: 'フロント正面' },
            { viewAngle: 'side', label: 'サイド' },
            { viewAngle: 'rear', label: 'リア' },
            { viewAngle: 'front_angled_7_3', label: 'フロント斜め' },
            { viewAngle: 'rear_angled_7_3', label: 'リア斜め' }
          ]
        }
      },
    };

    return aishaUser;
  } catch (error) {
    console.error('MyGarage token validation error:', error);
    return null;
  }
}; 
// MyGarage→AISHA SSO: 署名URL(mg_uid/ts/sig)を検証し、成功なら mygarage_user クッキーを発行する
// 検証(HMAC)はバックエンドで実施（共有シークレットはサーバ側のみ）。成功後は通常ログインと同じ状態になる。
export const mygarageSsoVerify = async (
  mg_uid: string,
  ts: string,
  sig: string,
): Promise<boolean> => {
  try {
    const res = await fetch(`${AISHA_API_BASE_URL}/mygarage-auth/sso/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mg_uid, ts, sig }),
    });
    if (!res.ok) {
      console.warn('SSO署名検証に失敗:', res.status);
      return false;
    }
    const data = await res.json();
    if (!data.success || !data.user_id) {
      return false;
    }
    const cookieObj = { id: String(data.user_id), email: data.email || '', name: data.name || '' };
    // iframe(第三者コンテキスト)ではクッキーが弾かれるため sessionStorage にも保存（embedセッション保持）
    try {
      sessionStorage.setItem('mygarage_user', JSON.stringify(cookieObj));
    } catch (e) {
      console.warn('sessionStorage保存失敗:', e);
    }
    // first-party用にクッキーも発行（第三者iframe対策で SameSite=None; Secure）
    try {
      const expires = new Date();
      expires.setDate(expires.getDate() + 30);
      document.cookie = `mygarage_user=${encodeURIComponent(JSON.stringify(cookieObj))};expires=${expires.toUTCString()};path=/;SameSite=None;Secure`;
    } catch (e) {
      console.warn('cookie発行失敗:', e);
    }
    console.log('✅ SSOログイン成功: user_id=', data.user_id);
    return true;
  } catch (e) {
    console.error('SSO検証エラー:', e);
    return false;
  }
};
